import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AttachmentsForm from "../../../../../../../../../../components/Attachment/AttachmentsForm";
import {
	TagsFormInput,
	mapTagsToAcceptableByBackendFormat,
} from "../../../../../../../../../../components/Tag/TagsFormInput";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { BundleApi, BundleItemApi } from "../../../../../../../../../../services/Api/Organization/types";
import ItemsError from "../../../../components/ItemsError";
import BundleBasicInfoForm from "./BundleBasicInfoForm";
import BundleFormItemsTable from "./BundleFormItemsTable/FormItemsTable/BundleFormItemsTable";

interface Props {
	bundle?: BundleApi;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const BundleForm = ({ bundle, customFieldsConfig }: Props) => {
	const form = useForm<BundleApi>({
		criteriaMode: "all",
		defaultValues: {
			...bundle,
			items: !bundle?.items
				? [{ position: 0 } as BundleItemApi]
				: [...bundle.items, { position: bundle.items.length }],
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], bundle?.custom_fields || []),
			bundled_at: bundle?.bundled_at || formatDateToString(new Date()),
		},
	});
	const { t } = useTranslation();
	const { formState, handleSubmit, setError } = form;
	const [loading, setLoading] = useState(false);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [formErrors, setFormErrors] = useState<ApiError[]>([]);
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	const getParsedDataForm = (data: BundleApi) => {
		data.items = [...data.items.filter((item) => item?.product_id || item?.quantity || item?.quantity_produced)];
		data.items = [...data.items].map((item) => ({
			...item,
			items: (item.items || []).filter((itemItem) => itemItem.product_id || itemItem.quantity),
		}));
		if (data.tags) data.tags = mapTagsToAcceptableByBackendFormat(data.tags);

		return data;
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		const parsedData = getParsedDataForm(data);
		setLoading(true);
		try {
			let res: BundleApi;
			if (data.id) {
				res = await apiOrganization.updateBundle(parsedData);
			} else {
				res = await apiOrganization.createBundle(parsedData);
			}
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			history.push(`/${organization.id}/documents/bundles/${res.id}`);
		} catch (err) {
			handleError.form(err, setError, addFlash, ["items"]);
			const exceptionErrors = handleException(err);
			setFormErrors([...exceptionErrors]);
		}

		setLoading(false);
	});

	return (
		<FormDirty
			formState={formState}
			onSubmit={(event) => {
				onSubmit(event);
			}}
			loading={loading}
		>
			<BundleBasicInfoForm form={form} setErrors={setErrors} />
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={bundle?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
			<ItemsError
				apiErrors={formErrors}
				formErrors={formState.errors}
				register={form.register}
				watch={form.watch}
				setErrors={setErrors}
			/>
			<BundleFormItemsTable form={form} />
			<h5>{t("lib:common.word.advanced")}</h5>
			<div>
				<TagsFormInput form={form} />
				<span className="mt-4 d-block">{t("common.word.attachments")}</span>
				<AttachmentsForm form={form} />
			</div>
		</FormDirty>
	);
};

export default BundleForm;
